/**
 * Закрытие достигнутых целей — в каркасе приложения, а не на экране целей.
 *
 * Цель, дошедшая до ста процентов, отмечается достигнутой сама: ждать, пока
 * человек нажмёт кнопку, незачем — он уже сделал всё, ради чего цель заводилась,
 * и приложение об этом знает.
 *
 * Проверка жила в эффекте `GoalsPage`, и цель с закрытым последним шагом
 * оставалась `active` с пустым `achievedAt`, пока человек не откроет «Цели»: она
 * продолжала считаться активной везде, поздравление не приходило вовсе, а дата
 * достижения в итоге получала момент захода на экран.
 *
 * Разложить проверку по местам, где меняется прогресс, было бы дешевле, но это
 * та же форма, из-за которой ошибка и появилась: поведение, пришитое к
 * конкретным местам. Сегодня их шесть, завтра появится седьмое — и о нём
 * забудут. Здесь место одно.
 *
 * Цена платится узким запросом. Экран целей читает всё сразу, включая отметки
 * привычек без окна — таблицу, которая растёт по строке в день навсегда;
 * подписать на неё каждый экран нельзя. Поэтому здесь: сначала активные цели —
 * их единицы, — и только если они есть, читается привязанное к ним. У кого
 * привычки к целям не привязаны, таблица отметок не открывается вовсе.
 */

import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useRef } from 'react';

import { goalProgress, goalsReadyToAchieve } from '@/entities/goal/model/goal-service';
import { goalsRepo } from '@/entities/goal/model/repository';
import { habitProgressByHabit, isHabitActive } from '@/entities/habit/model/habit-service';
import { useProfile } from '@/entities/profile/model/use-profile';
import { celebrate } from '@/features/celebration/model/celebration-store';
import { db } from '@/shared/db/database';
import type { Goal, Habit, HabitEntry, Task } from '@/shared/db/schema';
import { getCurrentUserId } from '@/shared/db/session-context';
import { toast } from '@/shared/ui/toast';

interface Snapshot {
  goals: Goal[];
  tasks: Task[];
  habits: Habit[];
  entries: HabitEntry[];
}

const EMPTY: Snapshot = { goals: [], tasks: [], habits: [], entries: [] };

/** Читает только то, без чего решение принять нельзя. */
async function loadActiveGoals(): Promise<Snapshot> {
  const userId = getCurrentUserId();
  if (!userId) return EMPTY;

  const goals = (await db.goals.where('[userId+status]').equals([userId, 'active']).toArray()).filter(
    (goal) => goal.deletedAt === null,
  );
  // Активных целей нет — дальше читать нечего, и остальные таблицы не трогаем.
  if (goals.length === 0) return EMPTY;

  const goalIds = new Set(goals.map((goal) => goal.id));

  const [allTasks, allHabits] = await Promise.all([
    db.tasks.where('userId').equals(userId).toArray(),
    db.habits.where('userId').equals(userId).toArray(),
  ]);

  const tasks = allTasks.filter(
    (task) => task.deletedAt === null && task.goalId !== null && goalIds.has(task.goalId),
  );
  const habits = allHabits.filter(
    (habit) => habit.deletedAt === null && habit.goalId !== null && goalIds.has(habit.goalId),
  );

  /*
    Отметки привычек — самая дорогая часть: таблица растёт по строке в день и
    никогда не обрезается. Читаются они только там, где могут повлиять на ответ.

    Прогресс цели — это (закрытые задачи + доли привычек) / (задачи + привычки).
    Доля привычки не больше единицы, поэтому до ста процентов цель не дотянет,
    пока открыта хотя бы одна её задача, — и доли считать незачем. У кого в
    активных целях есть незакрытые задачи (обычное состояние), таблица отметок
    не открывается вовсе.
  */
  const openTaskGoals = new Set(
    tasks.filter((task) => task.status === 'todo').map((task) => task.goalId),
  );
  const needEntries = habits.filter((habit) => !openTaskGoals.has(habit.goalId));

  if (needEntries.length === 0) return { goals, tasks, habits, entries: [] };

  const entries = (
    await Promise.all(
      needEntries.map((habit) => db.habitEntries.where('habitId').equals(habit.id).toArray()),
    )
  )
    .flat()
    .filter((entry) => entry.deletedAt === null);

  return { goals, tasks, habits, entries };
}

export function useGoalSettlement(): void {
  const profile = useProfile();
  const celebrationsEnabled = profile?.celebrationsEnabled !== false;

  const data = useLiveQuery(loadActiveGoals, []);

  /*
    Защита от повторной отметки в промежутке между записью и приходом
    обновлённых данных — иначе на цель ушло бы несколько записей и несколько
    поздравлений подряд.
  */
  const handled = useRef(new Set<string>());

  useEffect(() => {
    if (!data || data.goals.length === 0) return;

    const habitProgress = habitProgressByHabit(data.habits, data.entries);

    const tasksOf = (goalId: string) => data.tasks.filter((task) => task.goalId === goalId);
    const sharesOf = (goalId: string) =>
      data.habits
        .filter((habit) => habit.goalId === goalId && isHabitActive(habit))
        .map((habit) => habitProgress.get(habit.id)?.value ?? 0);

    const ready = goalsReadyToAchieve(data.goals, (goal) =>
      goalProgress(goal, tasksOf(goal.id), sharesOf(goal.id)),
    ).filter((goal) => !handled.current.has(goal.id));

    if (ready.length === 0) return;

    for (const goal of ready) handled.current.add(goal.id);

    void (async () => {
      for (const goal of ready) {
        await goalsRepo.update(goal.id, { status: 'achieved', achievedAt: Date.now() });

        // Отмена рядом с поздравлением: если расчёт разошёлся с ощущением,
        // цель возвращается в работу одним нажатием.
        toast.undo(`Цель «${goal.title}» достигнута`, () => {
          handled.current.delete(goal.id);
          void goalsRepo.update(goal.id, { status: 'active', achievedAt: null });
        });
      }

      if (celebrationsEnabled) celebrate('day');
    })();
  }, [data, celebrationsEnabled]);
}
